// Header/footer content of jobnet.works (sid wrktp), mirrored from the main site.
// The main site exists in English, German and Ukrainian with the same (English) slugs
// in every language, so every other portal language falls back to English.

export type WrktpLang = 'en' | 'de' | 'uk';

export interface WrktpLink {
  text: string
  path: string
}

type WrktpNavKey = 'welcome' | 'recruitment' | 'jobPlacement' | 'avgsMpav' | 'itOutstaffing' | 'aboutUs' | 'contact';

const WRKTP_NAV_PATHS: Record<WrktpNavKey, string> = {
  welcome: 'welcome',
  recruitment: 'recruitment',
  jobPlacement: 'job-placement',
  avgsMpav: 'avgs-mpav',
  itOutstaffing: 'it-outstaffing',
  aboutUs: 'about-us',
  contact: 'contact',
};

const WRKTP_PRIVACY_PATH = 'content/privacy-policy';

interface WrktpTexts {
  nav: Record<WrktpNavKey, string>
  privacy: string
  imprint: string
  mainNavLabel: string
  toggleNavLabel: string
  pagesHeading: string
  servicesHeading: string
  companyHeading: string
  legalLabel: string
  description: string
  registeredOffice: string
  hotlineGermany: string
  hotlineAustria: string
  company: {
    company: string
    office: string
    azav: string
    azavValue: string
    certBody: string
    hotlineDe: string
    hotlineAt: string
  }
  copyright: string
}

const wrktpTexts: Record<WrktpLang, WrktpTexts> = {
  en: {
    nav: {
      welcome: 'Welcome',
      recruitment: 'Recruitment',
      jobPlacement: 'Job Placement',
      avgsMpav: 'AVGS MPAV',
      itOutstaffing: 'IT Outstaffing',
      aboutUs: 'About Us',
      contact: 'Contact',
    },
    privacy: 'Privacy policy',
    imprint: 'Imprint',
    mainNavLabel: 'Main navigation',
    toggleNavLabel: 'Toggle navigation',
    pagesHeading: 'Pages',
    servicesHeading: 'Services',
    companyHeading: 'Company information',
    legalLabel: 'Legal',
    description: 'Job placement, recruitment, IT outstaffing, and digital collaboration on one shared platform — '
      + 'for more and better employment.',
    registeredOffice: 'Registered office',
    hotlineGermany: 'Germany',
    hotlineAustria: 'Austria',
    company: {
      company: 'Company',
      office: 'Registered office',
      azav: 'AZAV certificate',
      azavValue: 'Nr. TZ-104194 (valid until 9 August 2031)',
      certBody: 'Certification body',
      hotlineDe: 'Hotline Germany',
      hotlineAt: 'Hotline Austria',
    },
    copyright: 'AZAV-Zertifikats-Nr. TZ-104194, valid until 9 August 2031 (Certuria Certification Germany GmbH)',
  },
  de: {
    nav: {
      welcome: 'Willkommen',
      recruitment: 'Personalvermittlung',
      jobPlacement: 'Arbeitsvermittlung',
      avgsMpav: 'AVGS MPAV',
      itOutstaffing: 'IT-Outstaffing',
      aboutUs: 'Über uns',
      contact: 'Kontakt',
    },
    privacy: 'Datenschutz',
    imprint: 'Impressum',
    mainNavLabel: 'Hauptnavigation',
    toggleNavLabel: 'Navigation umschalten',
    pagesHeading: 'Seiten',
    servicesHeading: 'Leistungen',
    companyHeading: 'Unternehmensangaben',
    legalLabel: 'Rechtliches',
    description: 'Arbeitsvermittlung, Personalvermittlung, IT-Outstaffing und digitale Zusammenarbeit '
      + 'auf einer gemeinsamen Plattform — für mehr und gute Beschäftigung.',
    registeredOffice: 'Sitz der Gesellschaft',
    hotlineGermany: 'Deutschland',
    hotlineAustria: 'Österreich',
    company: {
      company: 'Unternehmen',
      office: 'Sitz',
      azav: 'AZAV-Zertifikat',
      azavValue: 'Nr. TZ-104194 (gültig bis 09.08.2031)',
      certBody: 'Zertifizierungsstelle',
      hotlineDe: 'Hotline DE',
      hotlineAt: 'Hotline AT',
    },
    copyright: 'AZAV-Zertifikats-Nr. TZ-104194, gültig bis 09.08.2031 (Certuria Certification Germany GmbH)',
  },
  uk: {
    nav: {
      welcome: 'Ласкаво просимо',
      recruitment: 'Персональний підбір',
      jobPlacement: 'Працевлаштування',
      avgsMpav: 'AVGS MPAV',
      itOutstaffing: 'IT-аутстафінг',
      aboutUs: 'Про нас',
      contact: 'Контакт',
    },
    // jobnet.works itself keeps the legal links in English on the Ukrainian pages
    privacy: 'Privacy policy',
    imprint: 'Imprint',
    mainNavLabel: 'Основна навігація',
    toggleNavLabel: 'Перемкнути навігацію',
    pagesHeading: 'Сторінки',
    servicesHeading: 'Послуги',
    companyHeading: 'Відомості про компанію',
    legalLabel: 'Правова інформація',
    description: 'Працевлаштування, персональний підбір, IT-аутстафінг та цифрова співпраця на одній спільній '
      + 'платформі — для більшої та якіснішої зайнятості.',
    registeredOffice: 'Юридична адреса',
    hotlineGermany: 'Німеччина',
    hotlineAustria: 'Австрія',
    company: {
      company: 'Компанія',
      office: 'Адреса',
      azav: 'Сертифікат AZAV',
      azavValue: 'Nr. TZ-104194 (дійсний до 9 серпня 2031 р.)',
      certBody: 'Орган сертифікації',
      hotlineDe: 'Гаряча лінія DE',
      hotlineAt: 'Гаряча лінія AT',
    },
    copyright: 'AZAV-Zertifikats-Nr. TZ-104194, дійсний до 9 серпня 2031 р. (Certuria Certification Germany GmbH)',
  },
};

type WrktpContent = Omit<WrktpTexts, 'nav' | 'privacy'> & {
  nav: Record<WrktpNavKey, WrktpLink>
  privacy: WrktpLink
};

const withPaths = (texts: WrktpTexts): WrktpContent => ({
  ...texts,
  nav: (Object.keys(WRKTP_NAV_PATHS) as WrktpNavKey[]).reduce((nav, key) => ({
    ...nav,
    [key]: { text: texts.nav[key], path: WRKTP_NAV_PATHS[key] },
  }), {} as Record<WrktpNavKey, WrktpLink>),
  privacy: { text: texts.privacy, path: WRKTP_PRIVACY_PATH },
});

export const wrktpContent: Record<WrktpLang, WrktpContent> = {
  en: withPaths(wrktpTexts.en),
  de: withPaths(wrktpTexts.de),
  uk: withPaths(wrktpTexts.uk),
};

export const WRKTP_IMPRINT_URL = 'https://jobnet.ag/JobnetAG/Kontakt/impressum.php';

export const wrktpLang = (lang: string): WrktpLang => (lang === 'de' || lang === 'uk' ? lang : 'en');

// Header and footer link to the new jobnet.works site exactly as its own header/footer do
export const WRKTP_SITE_URL = 'https://jobnet.works';

export const wrktpTalentSpaceUrl = (lang: WrktpLang): string => `https://talentspace.jobnet.works/?lang=${lang}`;

export const wrktpUrl = (lang: WrktpLang, path: string): string => {
  const base = `${WRKTP_SITE_URL}/${lang}`;
  return path ? `${base}/${path}` : base;
};
